import type { RefObject } from 'react';

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useRegisterOpenModalAfterEnter } from 'cpool-components/general/Modal/hooks';
import { RxCross2 } from 'react-icons/rx';

import { cn } from '~/utils/cnTailwind';

type DialogBoxProps = {
    title: React.ReactNode;
    children: React.ReactNode;
    open: boolean;
    onClose: () => void;
    initialFocusRef?: RefObject<HTMLDivElement | null>;
    dataTestId?: string;
    panelClassName?: string;
};

const DialogBox: React.FC<DialogBoxProps> = ({
    children,
    dataTestId,
    initialFocusRef,
    onClose,
    open,
    panelClassName,
    title,
}) => {
    const onAfterEnter = useRegisterOpenModalAfterEnter(open);

    return (
        <Transition show={open}>
            <Dialog as="div" onClose={onClose} initialFocus={initialFocusRef}>
                <TransitionChild
                    as="div"
                    enter="transition ease-out duration-150 transform"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition ease-out duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="fixed inset-0 bg-black bg-opacity-50 z-50"
                />
                <TransitionChild
                    as="div"
                    enter="transition ease-out duration-150 transform"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-out duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                    afterEnter={onAfterEnter}
                    className="fixed inset-0 max-h-full overflow-hidden flex justify-center items-center px-4 py-4 lg:px-6 transform z-50"
                >
                    <DialogPanel
                        as="div"
                        className={cn(
                            'relative w-full max-w-[832px] p-4 max-h-[90%] overflow-y-auto bg-white shadow-2',
                            panelClassName,
                        )}
                        ref={initialFocusRef}
                        data-testid={dataTestId}
                    >
                        <span className="absolute top-2 right-2 text-midnight cursor-pointer" onClick={onClose}>
                            <RxCross2 size={18} />
                        </span>
                        {title ? (
                            <DialogTitle as="h6" className="mb-3 text-xl font-medium text-midnight">
                                <>{title}</>
                            </DialogTitle>
                        ) : null}
                        <>{children}</>
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
};

export default DialogBox;
